import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { addDoc, collection, doc, getDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";

const REQUEST_TYPES = [
  ["casual", "Make it more casual"],
  ["dress_up", "Dress it up"],
  ["work", "Style it for work"],
  ["season", "Style it for a season"],
  ["color", "Find a color combination"],
  ["wear_again", "Help me wear it again"],
  ["open", "Ask my own question"],
];

export default function CreateStyleRequestPage({ user }) {
  const [searchParams] = useSearchParams();
  const [pieces, setPieces] = useState([]);
  const [pieceId, setPieceId] = useState(searchParams.get("piece") || "");
  const [requestType, setRequestType] = useState("wear_again");
  const [prompt, setPrompt] = useState("");
  const [audience, setAudience] = useState("similar");
  const [wearingPhoto, setWearingPhoto] = useState(null);
  const [fitContext, setFitContext] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadPieces = async () => {
      const snapshot = await getDocs(collection(db, "users", user.uid, "wardrobe"));
      setPieces(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
    };
    loadPieces();
  }, [user.uid]);

  const selectedPiece = useMemo(
    () => pieces.find((piece) => piece.id === pieceId),
    [pieces, pieceId]
  );

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!selectedPiece || !prompt.trim()) return;
    setSubmitting(true);
    try {
      let wearingPhotoUrl = "";
      if (wearingPhoto) {
        if (wearingPhoto.size > 5 * 1024 * 1024) {
          alert("Please choose a wearing photograph smaller than 5 MB.");
          setSubmitting(false);
          return;
        }
        const wearingPhotoReference = ref(
          storage,
          `styleRequestFitPhotos/${user.uid}/${Date.now()}_${wearingPhoto.name}`
        );
        await uploadBytes(wearingPhotoReference, wearingPhoto);
        wearingPhotoUrl = await getDownloadURL(wearingPhotoReference);
      }

      const profileSnapshot = await getDoc(doc(db, "users", user.uid));
      const profileName = profileSnapshot.exists() ? profileSnapshot.data().name : "";
      const requestRef = await addDoc(collection(db, "styleRequests"), {
        createdBy: user.uid,
        creatorName: profileName || user.displayName || "Style lover",
        pieceId: selectedPiece.id,
        pieceOwnerId: user.uid,
        pieceName: selectedPiece.name,
        pieceImageUrl: selectedPiece.imageUrl || "",
        pieceCategory: selectedPiece.category || "",
        pieceTags: selectedPiece.tags || [],
        requestType,
        prompt: prompt.trim(),
        wearingPhotoUrl,
        fitContext: fitContext.trim(),
        audience,
        visibility: "public",
        status: "open",
        responseCount: 0,
        helpfulResponseId: null,
        createdAt: serverTimestamp(),
      });
      navigate(`/style-requests/${requestRef.id}`);
    } catch (error) {
      console.error("Could not create request", error);
      alert("We could not post your request. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f8f3ef] px-6 py-14 text-stone-800">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-rose-700">Style this with me</p>
        <h1 className="mt-3 text-4xl font-semibold text-stone-900">Ask about one loved piece</h1>
        <p className="mt-4 leading-7 text-stone-600">
          Choose something from your wardrobe and tell the community what kind of
          inspiration would be useful.
        </p>

        {pieces.length === 0 ? (
          <div className="mt-10 border border-dashed border-stone-400 p-10 text-center">
            Add a piece to My Wardrobe before creating a styling request.
          </div>
        ) : (
          <form onSubmit={submitRequest} className="mt-10 space-y-8 bg-white p-6 md:p-10">
            <label className="block text-sm font-medium text-stone-700">
              Which piece are we styling?
              <select
                value={pieceId}
                onChange={(event) => setPieceId(event.target.value)}
                required
                className="mt-2 w-full border border-stone-300 bg-white px-4 py-3"
              >
                <option value="">Choose a piece</option>
                {pieces.map((piece) => (
                  <option key={piece.id} value={piece.id}>
                    {piece.name}
                  </option>
                ))}
              </select>
            </label>

            {selectedPiece?.imageUrl && (
              <img
                src={selectedPiece.imageUrl}
                alt={selectedPiece.name}
                className="max-h-80 w-full object-cover"
              />
            )}

            <label className="block text-sm font-medium text-stone-700">
              What kind of help would you like?
              <select
                value={requestType}
                onChange={(event) => setRequestType(event.target.value)}
                className="mt-2 w-full border border-stone-300 bg-white px-4 py-3"
              >
                {REQUEST_TYPES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <section className="border border-rose-200 bg-rose-50 p-5">
              <p className="text-sm font-medium text-stone-800">
                Help stylists personalize their suggestions — optional
              </p>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                You may add a photograph wearing the piece or describe how it fits.
                Your face never needs to be visible—crop the image however you prefer.
              </p>
              <p className="mt-2 text-xs leading-5 text-rose-800">
                If added, this photograph and description will be visible to everyone
                who can view this public styling request.
              </p>

              <label className="mt-5 block text-sm font-medium text-stone-700">
                Optional photograph wearing the piece
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setWearingPhoto(event.target.files[0] || null)}
                  className="mt-2 block w-full border border-stone-300 bg-white p-3 text-sm"
                />
                <span className="mt-1 block text-xs font-normal text-stone-500">
                  Face cropped, shoulders down, mirror photo, or any framing that feels comfortable · maximum 5 MB
                </span>
              </label>

              <label className="mt-5 block text-sm font-medium text-stone-700">
                Optional fit, comfort, or styling context
                <textarea
                  value={fitContext}
                  onChange={(event) => setFitContext(event.target.value)}
                  maxLength={400}
                  rows={4}
                  placeholder="For example: It feels formal on me, I prefer more coverage, and I want to wear it comfortably for daytime."
                  className="mt-2 w-full border border-stone-300 bg-white px-4 py-3 leading-6"
                />
                <span className="mt-1 block text-right text-xs font-normal text-stone-400">
                  {fitContext.length}/400
                </span>
              </label>
            </section>

            <label className="block text-sm font-medium text-stone-700">
              Your styling question
              <textarea
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                required
                maxLength={500}
                rows={5}
                placeholder="How could I wear this dress in early fall without making it feel too formal?"
                className="mt-2 w-full border border-stone-300 px-4 py-3 leading-6"
              />
              <span className="mt-1 block text-right text-xs text-stone-400">
                {prompt.length}/500
              </span>
            </label>

            <label className="block text-sm font-medium text-stone-700">
              Who is this question most relevant to?
              <select
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
                className="mt-2 w-full border border-stone-300 bg-white px-4 py-3"
              >
                <option value="similar">People with the same or similar pieces</option>
                <option value="everyone">Everyone</option>
              </select>
            </label>

            <section className="border border-rose-300 bg-rose-50 p-5">
              <p className="text-sm font-medium text-stone-800">This style question will be public</p>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                It will appear under <strong>Browse style questions</strong>, where
                community members can view the piece, read the context you choose to
                share, and offer styling ideas.
              </p>
              <p className="mt-2 text-xs leading-5 text-rose-800">
                Share only photographs and information you are comfortable making
                public. Your email and private wardrobe remain hidden.
              </p>
            </section>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-stone-900 px-6 py-3 text-white hover:bg-rose-800 disabled:opacity-50"
            >
              {submitting ? "Posting…" : "Invite the community to style it"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
